import { useQuery } from '@tanstack/react-query';
import {
  reportTemplateService,
  type ReportTemplateListFilters,
} from '@/services/report-template.service';

/**
 * Query keys for the Report Designer (B6.9e).
 *
 * Shaped like `procedureKeys` so invalidation reads the same across the two
 * template modules. `versions` hangs off the template detail rather than off the
 * family slug: the API answers the family question from any member id, and
 * keying by id is what lets a mutation on one version invalidate the history
 * that version is displayed in.
 */
export const reportTemplateKeys = {
  all: () => ['report-templates'] as const,
  lists: () => [...reportTemplateKeys.all(), 'list'] as const,
  list: (f: ReportTemplateListFilters) => [...reportTemplateKeys.lists(), f] as const,
  details: () => [...reportTemplateKeys.all(), 'detail'] as const,
  detail: (id: string) => [...reportTemplateKeys.details(), id] as const,
  versions: (id: string) => [...reportTemplateKeys.detail(id), 'versions'] as const,
} as const;

export function useReportTemplateList(filters: ReportTemplateListFilters) {
  return useQuery({
    queryKey: reportTemplateKeys.list(filters),
    queryFn: () => reportTemplateService.list(filters),
    // A 403 is a settled answer, not a transient failure: retrying it delays
    // the permission-denied state the user needs to see.
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * One template with its full design.
 *
 * `enabled: !!id` is what keeps §29 true — a designer route with no id, or a
 * panel that has not been opened, issues no request at all.
 *
 * A DRAFT is short-lived truth (it is being edited); a PUBLISHED or ARCHIVED
 * version is immutable and can be cached far longer. Mirrors
 * `useProcedureTemplate`.
 */
export function useReportTemplate(id: string | undefined) {
  return useQuery({
    queryKey: reportTemplateKeys.detail(id ?? ''),
    queryFn: () => reportTemplateService.get(id!),
    enabled: !!id,
    retry: false,
    staleTime: (query) => (query.state.data?.status === 'DRAFT' ? 10_000 : 5 * 60 * 1000),
  });
}

export function useReportTemplateVersions(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: reportTemplateKeys.versions(id ?? ''),
    queryFn: () => reportTemplateService.getVersions(id!),
    // Two gates, deliberately: the id must exist AND the caller must want the
    // data. The designer's version panel is collapsed by default, and a
    // collapsed panel must not fetch (§29).
    enabled: !!id && enabled,
    retry: false,
    staleTime: 30_000,
  });
}
